import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDepartments, deleteDepartment } from '../../redux/actions/department/departmentAction';
import DataTable from 'react-data-table-component';
import { Box, IconButton, TextField, Typography, Button, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { confirmDelete, showSuccess } from '../../utils/alertUtils'; 
import { toast } from 'react-toastify';

const DepartmentTable = ({ onEdit }) => {
  const dispatch = useDispatch();
  const { departments, deleteLoading } = useSelector((state) => state.department);
  const { user } = useSelector((state) => state.login);
  const [filterText, setFilterText] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  const handleDelete = async (id) => {
      const result = await confirmDelete('Are you sure?', 'This will delete the department permanently!');
      if (result.isConfirmed) {
        setDeletingId(id);
        try {
          await dispatch(deleteDepartment(id));
          showSuccess('Deleted!', 'The department has been deleted.');
        } catch (error) {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete department';
          toast.error(errorMessage);
        } finally {
          setDeletingId(null);
        }
      }
    };

  const handleNew = () => {
    onEdit(null); // This will reset the form and show it in "Add" mode
  };

  const filteredDepartments = departments.filter((dep) =>
    (dep.name || '').toLowerCase().includes(filterText.toLowerCase())
  );

  const columns = [
    {
      name: <Typography fontWeight="bold">Name</Typography>,
      selector: (row) => row.name,
      sortable: true,
    },
   {
      name: <Typography fontWeight="bold">Status</Typography>,
      selector: (row) => row.status,
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold">Process Order No</Typography>,
      selector: (row) => row.process_order_no,
      sortable: true,
      center: true,
    },
     {
      name: <Typography fontWeight="bold">Manual Remark</Typography>,
      selector: (row) => row.manual_remark,
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold">Inactive Remark</Typography>,
      selector: (row) => row.inactive_remark_category,
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold">Enable Stone Columns</Typography>,
      selector: (row) => row.enable_stone_cols === 1 ? 'Yes' : 'No',
      sortable: true,
      center: true,
    },
    {
      name: <Typography fontWeight="bold">Enable Pearl Columns</Typography>,
      selector: (row) => row.enable_pearl_cols === 1 ? 'Yes' : 'No',
      sortable: true,
      center: true,
      cell: (row) => (
        <span style={{ color: row.enable_stone_cols === 1 ? undefined : '#aaa' }}>
          {row.enable_pearl_cols === 1 ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: <Typography fontWeight="bold">End Dept</Typography>,
      selector: (row) => row.is_end_dept === 1 ? 'Yes' : 'No',
      sortable: true,
      center: true,
      cell: (row) => (
        <span style={{ color: row.enable_stone_cols === 1 ? undefined : '#aaa' }}>
          {row.is_end_dept === 1 ? 'Yes' : 'No'}
        </span>
      ),
    },
    ...(user?.role === 'Admin' ? [{
      name: <Typography fontWeight="bold">Actions</Typography>,
      cell: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => onEdit(row)}
            disabled={deleteLoading && deletingId === row.id}
          >
            <EditIcon fontSize="small" sx={{ color: '#2196f3' }}/>
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => handleDelete(row.id)}
            disabled={deleteLoading && deletingId === row.id}
          >
            {deleteLoading && deletingId === row.id ? (
              <CircularProgress size={16} color="error" />
            ) : (
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }}/>
            )}
          </IconButton>
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }] : []),
  ];

  return (
   
  <Box>
    {/* Header Row with Title & Search */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Departments
        </Typography>
        {user?.role === 'Admin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
            size="small"
            disabled={deleteLoading}
            sx={{ 
              backgroundColor: '#2196f3',
              '&:hover': {
                backgroundColor: '#1976d2',
              },
            }}
          >
            New
          </Button>
        )}
      </Box>

      <TextField
        size="small"
        placeholder="Search department..."
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        disabled={deleteLoading}
        sx={{
          width: '250px',
          '& .MuiInputBase-root': {
            backgroundColor: 'transparent',
          },
        }}
      />
    </Box>

    {/* Table */}
    <Box sx={{ height: '400px', overflowY: 'auto' }}>
    <DataTable
      columns={columns}
      data={filteredDepartments}
      pagination
      highlightOnHover
      dense
      responsive
      noHeader
      progressPending={deleteLoading}
    />
  </Box>
  </Box>
);
};

export default DepartmentTable;
